import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import {
    Button,
    FormControl,
    Input,
    InputLabel,
} from '@mui/material';
import { withStyles } from '@mui/styles';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { UsersMultiTypeahead, VirtualMachineTypeahead } from '../Typeahead';
import { fetchLicenseIUAssignment, fetchLicenseUserAssignment, fetchLicenseVMAssignment } from '../../../actions';
import { applyFilters, resetApplyFilters, updateFilters } from '../../../actions/reviewLicenseFilters';
import PersistentStorage from '../../../services/storage';
import { LICENSES_TYPES_ID, mapReviewLicenseFiltersStateToQuery } from '../../../utils';
import LicensesIuMultiTypeahead from '../Typeahead/LicensesIuMultiTypeahead';
import WithCopyButton from '../TableCells/WithCopyButton';

const styles = theme => ({
    button: {
        margin: theme.spacing(0.5),
        marginTop: theme.spacing(2),
        flexGrow: 1
    },
    filterForm: {
        padding: theme.spacing(1)
    },
    controlButtons: {
        width: '100%',
        display: 'flex',
        justifyContent: 'space-around'
    },
    deleteIcon: {
        position: 'absolute',
        right: 0,
        top: '40%'
    },
});

class ReviewLicenseFilter extends Component {
    state = {
        licenseId: '',
    };

    componentDidMount() {
        const isData = localStorage.hasOwnProperty('transferDataToReviewPage');
        if (!isData) {
            history.back();
            return;
        }

        const { licenseId, licenseTypeId } = PersistentStorage.getItem('transferDataToReviewPage');
        this.setState(state => ({ ...state, licenseId, currentLicenseTypeId: licenseTypeId }));
    }

    componentDidUpdate() {
        const { currentLicenseTypeId } = this.state;
        const { licenseTypeId } = PersistentStorage.getItem('transferDataToReviewPage');

        if (licenseTypeId !== currentLicenseTypeId) this.setCurrentLicenseTypeId(licenseTypeId);
    }

    getFetch = (licenseTypeId, filters) => {
        switch (licenseTypeId) {
            case LICENSES_TYPES_ID.user:
                this.props.fetchLicenseUserAssignment(filters);
                break;
            case LICENSES_TYPES_ID.vm:
                this.props.fetchLicenseVMAssignment(filters);
                break;
            case LICENSES_TYPES_ID.infrastructureUnit:
                this.props.fetchLicenseIUAssignment(filters);
                break;
            default:
        }
    }

    handleResetClick = async () => {
        await this.props.resetApplyFilters();
        const { licenseId, licenseTypeId } = PersistentStorage.getItem('transferDataToReviewPage');
        const filters = mapReviewLicenseFiltersStateToQuery({ ...this.props.reviewLicenseFilters, licenseId });

        this.getFetch(licenseTypeId, filters);

    };

    handleClickApply = () => {
        const { reviewLicenseFilters } = this.props;

        let filters;

        if (!reviewLicenseFilters.licenseId) {
            filters = { ...reviewLicenseFilters, licenseId: PersistentStorage.getItem('transferDataToReviewPage').licenseId };
        } else {
            filters = reviewLicenseFilters;
        }
        const appliedFilters = mapReviewLicenseFiltersStateToQuery(filters);

        this.props.applyFilters(filters);

        this.getFetch(this.state.currentLicenseTypeId, appliedFilters);

    };

    handleChangeMultiSuggestion = name => suggestions => {
        this.props.updateFilters({
            ...this.props.reviewLicenseFilters,
            [name]: suggestions
        });
    };

    setCurrentLicenseTypeId = licenseTypeId => {
        this.setState({ currentLicenseTypeId: licenseTypeId });
    }

    render() {
        const { classes, reviewLicenseFilters, t } = this.props;
        const { currentLicenseTypeId } = this.state;

        return (
            <div
                className={classNames('filter', classes.filterForm)}
            >

                <WithCopyButton
                    title={this.state?.licenseId}
                    notCell
                >
                    <FormControl
                        fullWidth
                        margin="dense"
                    >
                        <InputLabel htmlFor="licenseId">
                            {t('licenseId')}
                        </InputLabel>

                        <Input
                            id="licenseId"
                            name="licenseId"
                            value={this.state?.licenseId}
                            disabled
                        />
                    </FormControl>
                </WithCopyButton>

                {currentLicenseTypeId === LICENSES_TYPES_ID.user && (
                    <FormControl
                        fullWidth
                        margin="dense"
                    >
                        <UsersMultiTypeahead
                            margin="none"
                            value={reviewLicenseFilters.users}
                            labelId="user"
                            onChangeSuggestion={this.handleChangeMultiSuggestion('users')}
                            animatedPlaceholder
                            t={t}
                        />
                    </FormControl>
                )}
                {currentLicenseTypeId === LICENSES_TYPES_ID.vm && (
                    <FormControl
                        fullWidth
                        margin="dense"
                    >
                        <VirtualMachineTypeahead
                            margin="none"
                            value={reviewLicenseFilters.virtualMachines}
                            labelId="vmTitle"
                            name="name"
                            onChangeSuggestion={this.handleChangeMultiSuggestion('virtualMachines')}
                            t={t}
                        />
                    </FormControl>

                )}
                {currentLicenseTypeId === LICENSES_TYPES_ID.infrastructureUnit && (
                    <FormControl
                        fullWidth
                        margin="dense"
                    >
                        <LicensesIuMultiTypeahead
                            margin="none"
                            value={reviewLicenseFilters.infrastructureUnits}
                            labelId="infrastructureUnit"
                            onChangeSuggestion={this.handleChangeMultiSuggestion('infrastructureUnits')}
                            animatedPlaceholder
                            t={t}
                        />
                    </FormControl>

                )}
                <div className={classes.controlButtons}>
                    <Button
                        variant="contained"
                        className={classes.button}
                        onClick={this.handleResetClick}
                    >
                        {t('reset')}
                    </Button>
                    <Button
                        variant="contained"
                        className={classes.button}
                        color="primary"
                        type="submit"
                        onClick={this.handleClickApply}
                    >
                        {t('apply')}
                    </Button>
                </div>

            </div>
        );
    }
}

ReviewLicenseFilter.propTypes = {
    resetApplyFilters: PropTypes.func,
    reviewLicenseFilters: PropTypes.object,
    applyFilters: PropTypes.func,
    updateFilters: PropTypes.func,
};

const mapStateToProps = ({ reviewLicenseFilters, infrastructure }) => ({
    reviewLicenseFilters: reviewLicenseFilters.currentFilters,
    locations: infrastructure.locations
});

export default compose(
    withTranslation('licenses'),
    connect(mapStateToProps, { fetchLicenseUserAssignment,
        fetchLicenseIUAssignment,
        fetchLicenseVMAssignment,
        updateFilters,
        applyFilters,
        resetApplyFilters }),
    withStyles(styles)
)(ReviewLicenseFilter);
